// The grant lifecycle (#418): what an organisation asked of this workspace, answered
// on its own row, behind a step-up to grant and none to decline or revoke.
//
// Read off `routes/grants.py`. Granting is two calls, as the remediation policy's
// revision is (`api/policy.ts`): `POST /auth/step-up` with purpose `workspace_grant`
// mints the proof, and the proof rides the answer as `step_up_grant`.

import { CAPABILITIES, type Capability } from "./customers";
import type { Api } from "./http";
import { STEP_UP_PATH, type StepUp } from "./policy";

export const GRANTS_PATH = "/api/v1/access-grants";
export const GRANT_STEP_UP_PURPOSE = "workspace_grant";

export type GrantState = "pending" | "granted" | "declined" | "revoked";

export interface GrantRow {
  id: string;
  org_id: string;
  org_name: string | null;
  org_kind: "partner" | "customer" | null;
  capability: Capability;
  state: GrantState;
  requested_at: string | null;
  requested_by: string | null;
  consented_at: string | null;
  consented_by: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
}

export interface GrantsRead {
  waiting: GrantRow[];
  standing: GrantRow[];
  past: GrantRow[];
}

/**
 * The kinds whose live grant lets the organisation read this workspace's full audit trail,
 * sign-ins and internal answers included (#517, the owner's decision of 2026-09-17), so the
 * answer screen says so. The read is the policy's, not this list's: `audit_org_read`
 * (0001's `workspace_scope`, applied to `audit` in 0006) opens on a live `investigate` grant,
 * and `tests/postgres/test_audit_grant_reach.py` fails when this list and the policy differ.
 */
export const OPENS_AUDIT_TRAIL: readonly Capability[] = ["investigate"];

/**
 * The kinds that do nothing without a live Investigate: they are used from inside the
 * workspace, and Investigate is how a partner enters it. The API refuses a request for one
 * without Investigate (`NEEDS_INVESTIGATE` in `customers.py`, #1198); the answer side may
 * still allow one, and the screen says it has no effect yet (#1197). The decision, and why
 * `payload_reads` is not among them, is #1187's.
 */
export const NEEDS_INVESTIGATE: readonly Capability[] = ["propose", "approve_writes", "scan"];

/** What the screen says beside a capability in `NEEDS_INVESTIGATE` while Investigate is not live. */
export const NO_EFFECT_YET = "Has no effect until Investigate is allowed.";

/** Whether `row`'s capability does nothing because its partner's Investigate is not live. */
export const waitsOnInvestigate = (row: GrantRow, investigateLive: boolean): boolean =>
  !investigateLive && NEEDS_INVESTIGATE.includes(row.capability);

/** The partners, by id, whose Investigate is allowed and not taken back. */
export const investigateLiveIn = (standing: GrantRow[]) => {
  const live = new Set(
    standing.filter((row) => row.capability === "investigate").map((row) => row.org_id),
  );
  return (orgId: string) => live.has(orgId);
};

export const readGrants = (api: Api): Promise<GrantsRead> => api.get<GrantsRead>(GRANTS_PATH);

/** Grant behind a step-up, or decline without one. */
export async function answerGrant(
  api: Api,
  grantId: string,
  decision: "granted" | "declined",
  stepUp: StepUp | null,
): Promise<{ id: string; capability: Capability; state: GrantState }> {
  let grant = "";
  if (decision === "granted") {
    if (stepUp === null || stepUp.credential === "") {
      throw new Error("Allowing a capability needs you to confirm it is you.");
    }
    const proved = await api.post<{ grant?: unknown }>(STEP_UP_PATH, {
      method: stepUp.method,
      credential: stepUp.credential,
      purpose: GRANT_STEP_UP_PURPOSE,
    });
    grant = typeof proved?.grant === "string" ? proved.grant : "";
  }
  return api.post(`${GRANTS_PATH}/${encodeURIComponent(grantId)}/answer`, {
    decision,
    ...(decision === "granted" ? { step_up_grant: grant } : {}),
  });
}

export const revokeGrant = (
  api: Api,
  grantId: string,
): Promise<{ id: string; capability: Capability; state: GrantState }> =>
  api.post(`${GRANTS_PATH}/${encodeURIComponent(grantId)}/revoke`);

/** The requester asks again for capabilities with no live row (#418). */
export const requestAgain = (
  api: Api,
  workspaceId: string,
  capabilities: Capability[],
): Promise<{ requested: Capability[] }> =>
  api.post(`/api/v1/customers/${encodeURIComponent(workspaceId)}/requests`, { capabilities });

export const orderedCapabilities = (rows: GrantRow[]): GrantRow[] =>
  [...rows].sort(
    (a, b) => CAPABILITIES.indexOf(a.capability) - CAPABILITIES.indexOf(b.capability),
  );
